/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAccessibleDropdown, useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { ICON_MENU_OPEN } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { SdsText, SdsTextWeight } from '../SdsText';
import { SourceSelectorProps } from './SourceSelector.props';
import { Source } from './SourceSelector.types';
import { SourceItem } from './elements/SourceItem';
import { SourceLauncher } from './elements/SourceLauncher';

/**
 * SDS source selector component.
 */
export const SourceSelector = (props: SourceSelectorProps): JSX.Element => {
  const { defaultSource, placeholderText, onRootSelect, onSourceSelect, sources } = props;

  const [selectedSource, setSelectedSource] = useState<Source | undefined>(defaultSource);

  const uniqueId = useUniqueId();
  const wrapperId = `sds-source-selector-wrapper--${uniqueId}`;
  const buttonId = `sds-source-selector-button--${uniqueId}`;

  const itemCount = sources.length + 1;

  const { accessibleDropdownButtonProps, accessibleDropdownItemProps, dropdownOpen, setDropdownOpen } =
    useAccessibleDropdown({
      dropdownSelector: `#${wrapperId}`,
      itemCount,
    });

  const wrapperClass = clsx(`sds-source-selector-wrapper`, `sds-form-input`);
  const selectFieldClass = clsx(`sds-source-selector`, {
    [DXP_ACTIVE_CLASS]: dropdownOpen,
  });
  const panelClass = clsx(`sds-source-selector__panel`, {
    [DXP_ACTIVE_CLASS]: dropdownOpen,
  });

  const handleSourceSelect = (source: Source): void => {
    setSelectedSource(source);
    setDropdownOpen(false);
    onSourceSelect(source);
  };

  const handleRootSelect = (): void => {
    setSelectedSource(undefined);
    setDropdownOpen(false);
    onRootSelect();
  };

  const getRootItem = (): JSX.Element => {
    return (
      <li className="sds-source-selector__item">
        <button {...accessibleDropdownItemProps[0]} onClick={handleRootSelect}>
          <div className="sds-source-selector__icon">
            <SdsIcon svg={ICON_MENU_OPEN} />
          </div>
          <SdsText fontWeight={SdsTextWeight.Semibold} text={placeholderText} />
        </button>
      </li>
    );
  };

  const getSourceItems = (): Array<JSX.Element> => {
    return sources.map((source, sourceIndex): JSX.Element => {
      const isSelectedSource = source.id === selectedSource?.id;

      return (
        <React.Fragment key={source.id}>
          <SourceItem
            accessibleProps={accessibleDropdownItemProps[sourceIndex + 1]}
            isSelected={isSelectedSource}
            onSelect={handleSourceSelect}
            source={source}
          />
        </React.Fragment>
      );
    });
  };

  return (
    <div className={wrapperClass} id={wrapperId}>
      <div className={selectFieldClass}>
        <SourceLauncher
          accessibleProps={accessibleDropdownButtonProps}
          buttonId={buttonId}
          placeholderText={placeholderText}
          selectedSource={selectedSource}
        />
        {dropdownOpen && (
          <div aria-labelledby={buttonId} className={panelClass}>
            <ul className="sds-source-selector__list">
              {getRootItem()}
              {getSourceItems()}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
